<template>
  <LKRoadmapsFeedModalContainer
    content-class="modal--chat div-block LKRoadmapsFeedDialog"
    @close="closeDialogs"
  >
    <div
      class="chat__header cursor-pointer"
      @click="isActiveTheme = !isActiveTheme"
    >
      <v-icon
        size="20px"
        :class="{ 'mdi-rotate-180': isActiveTheme }"
        class="chat__header__icon-toggle"
        v-text="'mdi-chevron-up'"
      />
      <h3 class="text-h3 mod--chat">
        {{ options.event_name || 'Онлайн-чат' }}
      </h3>
      <v-icon
        size="20px"
        class="chat__header__close"
        @click.stop="closeDialogs"
        v-text="'mdi-close'"
      />
    </div>

    <div
      :class="{ 'is-active': isActiveTheme }"
      class="chat__theme"
    >
      <div class="chat__theme-inner">
        <template v-for="(item, index) in settings">
          <div v-if="item" :key="index" class="filter">
            <div class="filter__toggle">
              <div class="filter__option-group">
                <div
                  class="text-md mod--filter"
                  v-text="`${item.label}:`"
                />
                <div
                  class="text-md-bold mod--filter"
                  v-text="item.value"
                />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div ref="messageBox" class="chat__messages">
      <LKProgressCircular
        v-if="isLoadingData"
        overlay
        :size="50"
        style="align-items: start; padding-top: 30vh;"
      />

      <div v-for="node in allNodes" :key="node.data.id">
        <template v-if="node.isHistory">
          <div class="chat__messages__status">
            <template v-if="node.data.user">
              <span>{{ node.data.user.username }} изменил статус</span>
            </template>
            <template v-else>
              <span>Изменение статуса</span>
            </template>
            <span>
              меры на
              <nobr
                class="chat__messages__status-text"
                v-text="node.status"
              />
              <nobr> {{ node.createdAt }}</nobr>
            </span>
          </div>
        </template>

        <template v-else>
          <div
            class="chat__messages-stroke"
            :class="node.isHistory ? 'mod--system' : node.isCurrentUser ? 'mod--send' : 'mod--arrive'"
          >
            <template v-if="!node.isContinuous">
              <div class="text-md mod--chat-title">
                <template v-if="node.isCurrentUser">
                  Вы:
                </template>
                <template v-else>
                  {{ node.data.author.full_name }} | {{ node.data.author.username }}:
                </template>
              </div>
            </template>
            <div
              :class="{
                'is-new': node.isNew,
                chat__message_continuous: node.isContinuous,
              }"
              class="chat__message LKRoadmapsFeedDialog__message"
            >
              <div class="text-sm mod--chat" v-text="node.createdAt" />
              <div class="text-md" v-text="node.data.body" />
            </div>
          </div>
        </template>
      </div>

      <div v-if="commentNodes === 0">
        Нет сообщений
      </div>
    </div>

    <div class="chat__send">
      <form @submit.prevent="createComment">
        <textarea
          ref="textarea"
          v-model="body"
          maxlength="2000"
          placeholder="Введите сообщение"
          class="textarea--chat w-input"
          :disabled="isLoadingCreate"
        />
        <v-btn
          type="submit"
          color="webflow-color-blue-dark"
          block
          rounded
          height="50"
          class="chat__button"
          :loading="isLoadingCreate"
          :disabled="!body"
        >
          Отправить
        </v-btn>
      </form>
    </div>
  </LKRoadmapsFeedModalContainer>
</template>

<script>
/* eslint max-classes-per-file: 0 */
/**
 * @typedef {import('@/typings/ws/WSEventComments').WSEventComments} WSEventComments
 * @typedef {import('@/typings/ws/WSEventStatusChanged').WSEventStatusChanged} WSEventStatusChanged
 * @typedef {import('@/typings/api/RoadmapEventComment').RoadmapEventComment} RoadmapEventComment
 */

import last from 'lodash/last'
import { BaseTaskQuery, TaskTypes } from '@/api/BaseWS'
import { RoadmapEventCommentsApi, RoadmapEventsApi } from '@/api/resources'
import { dateFormatter } from '@/helpers/dateFormatter'
import { getRoadmapEventStatusesLabel } from '@/typings/api/RoadmapEvent'

import LKProgressCircular from '@/components/lk/LKProgressCircular.vue'
import LKRoadmapsFeedModalContainer from './LKRoadmapsFeedModalContainer.vue'


class CommentNode {
  /** @param {RoadmapEventComment} data */
  constructor(data, profile, isContinuous, isNew) {
    this.data = data
    this.isNew = isNew
    this.createdAt = CommentNode.formatTime(data.created_at)
    this.isCurrentUser = CommentNode.isCurrentUser(data.author, profile)
    this.isContinuous = isContinuous
  }

  static formatTime(datetime) {
    return dateFormatter(datetime, 'D MMMM YYYY / HH:mm')
  }

  static isCurrentUser(author, profile) {
    return profile.id === author?.id
  }
}

class HistoryNode extends CommentNode {
  isHistory = true;

  constructor(data, profile, isContinuous, isNew) {
    super(data, profile, isContinuous, isNew)
    this.status = getRoadmapEventStatusesLabel(data.status)
  }
}

export default {
  name: 'LKRoadmapsFeedDialog',
  components: {
    LKProgressCircular,
    LKRoadmapsFeedModalContainer,
  },
  props: {
    options: {
      type: Object,
    },
    profile: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isActiveTheme: false,
      commentNodes: [],
      historyNodes: [],
      body: null,
      isLoadingData: false,
      isLoadingCreate: false,
      roadmap: null,
      direction: null,
      school: null,
    }
  },
  computed: {
    settings() {
      const { options } = this
      const statusLabel = getRoadmapEventStatusesLabel(options.event_status)

      const settings = [
        { label: 'Дорожная карта', value: this.roadmap?.name },
        { label: 'Направление', value: this.direction?.name },
        { label: 'Мера', value: `${options.event_name} / ${statusLabel}` },
        { label: 'ОО', value: this.school?.name },
      ]

      return settings.filter((_) => _.value)
    },
    allNodes() {
      const { commentNodes } = this

      const historyStatusNodes = this.historyNodes
        .filter((_) => _.data.type === 'status')

      if (!historyStatusNodes.length) {
        return commentNodes
      }

      if (!commentNodes.length) {
        return historyStatusNodes
      }

      return [].concat(commentNodes, historyStatusNodes).sort((a, b) => {
        const left = a.data?.created_at || ''
        const right = b.data?.created_at || ''

        const dLeft = left ? new Date(left) : new Date()
        const dRight = right ? new Date(right) : new Date()

        return dLeft - dRight
      })
    },
  },
  created() {
    this.init()
  },
  mounted() {
    this.$refs.messageBox.focus()
    this.$refs.textarea?.focus()
  },
  beforeDestroy() {
    this.taskQuery?.destroy()
    this.taskQuery = null
  },
  methods: {
    async init() {
      this.isLoadingData = true
      this.getEventParents()

      this.taskQuery?.destroy()
      this.taskQuery = new BaseTaskQuery()

      const promises = [
        this.initComments().finally(this.addWSEventComments),
        this.initHistory().finally(this.addWSEventStatusChanged),
      ]

      await Promise.all(promises).catch(() => void 0)

      this.taskQuery?.addListener('reopen', this.reopen)
      this.isLoadingData = false
    },

    reopen() {
      (async () => {
        this.commentNodes = await this.onReopen(
          this.getComments,
          this.createCommentNodes,
          this.commentNodes,
        )
      })();

      (async () => {
        this.historyNodes = await this.onReopen(
          this.getHistory,
          this.createHistoryNodes,
          this.historyNodes,
        )
      })()
    },

    async onReopen(action, createNodes, nodes) {
      let pageSize = 1
      const { meta } = await action({ pageSize })
      pageSize = Math.abs(meta.count - nodes.length)

      if (pageSize) {
        const { data } = await action({ pageSize })
        const list = createNodes(data)
        return nodes.concat(list)
      }

      return nodes
    },

    async initComments() {
      const { data } = await this.getComments()
      const nodes = this.createCommentNodes(data)
      this.commentNodes = nodes
      this.updateCommentFrame(true)
    },

    async initHistory() {
      const { data } = await this.getHistory()
      this.historyNodes = this.createHistoryNodes(data)
      this.updateCommentFrame(true)
    },

    async getComments(options = {}) {
      const api = new RoadmapEventCommentsApi()
      const eventId = this.options?.event_id
      const params = { ...options, event_id: eventId }

      try {
        const { data, meta } = await api.getAll(params)
        return { data: data.reverse(), meta }
      } catch (e) {
        this.$notice.errors(e)
        return []
      }
    },

    async createComment() {
      this.isLoadingCreate = true
      const api = new RoadmapEventCommentsApi()

      const requestData = {
        event_id: this.options?.event_id,
        body: this.body,
      }

      try {
        await api.create(requestData)
        this.body = null
        this.updateCommentFrame(true)
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.isLoadingCreate = false
      }
    },

    async getEventParents() {
      const api = new RoadmapEventsApi()

      try {
        const { data } = await api.getParents(this.options?.event_id)
        this.roadmap = data.roadmap
        this.direction = data.direction
        this.school = data.school
      } catch (e) {
        this.$notice.errors(e)
      }
    },

    async getHistory(options = {}) {
      const api = new RoadmapEventsApi()
      const eventId = this.options?.event_id

      try {
        const { data, meta } = await api.getHistory(eventId, options)
        return { data, meta }
      } catch (e) {
        this.$notice.errors(e)
        return []
      }
    },

    closeDialogs() {
      this.$emit('close')
    },

    /** @param {RoadmapEventComment[]} comments */
    createCommentNodes(comments = []) {
      const { profile } = this

      return comments.map((comment, index) => {
        const isContinuous = (
          index > 0
          && comments[index - 1].author.id === comment.author.id
        )
        return new CommentNode(comment, profile, isContinuous, false)
      })
    },

    createHistoryNodes(histories = []) {
      const { profile } = this

      return histories
        .map((history) => new HistoryNode(history, profile))
    },

    addWSEventComments() {
      const task = {
        type: TaskTypes.eventComments,
        object_id: this.options.event_id,
        callback: this.onWSEventComments,
      }

      this.taskQuery?.subscribe(task)
    },

    addWSEventStatusChanged() {
      const task = {
        type: TaskTypes.eventStatusChanged,
        callback: this.onWSEventStatusChanged,
      }

      this.taskQuery?.subscribe(task)
    },

    /** @type {WSEventComments} */
    onWSEventComments(event) {
      const comment = event.message
      const { commentNodes, profile } = this

      const isContinuous = (
        last(commentNodes)?.data?.author?.id
        === comment.author.id
      )
      const commentNode = new CommentNode(comment, profile, isContinuous, true)
      commentNodes.push(commentNode)

      this.updateCommentFrame(false)
    },

    /** @param {WSEventStatusChanged} event */
    async onWSEventStatusChanged(event) {
      if (event.params.event_id !== this.options.event_id) return

      const params = { pageSize: 1, ordering: '-created_at' }
      const { data } = await this.getHistory(params)

      const nodes = this.createHistoryNodes(data)
      this.historyNodes = this.historyNodes.concat(nodes)
      this.updateCommentFrame(false)
    },

    updateCommentFrame(force = false) {
      const { messageBox } = this.$refs

      const isNeedScroll = force || (
        messageBox?.scrollTop + messageBox?.offsetHeight
        >= messageBox?.scrollHeight
      )

      if (isNeedScroll) {
        this.$nextTick(this.scrollToBottom)
      }
    },

    scrollToBottom() {
      if (!this.$refs.messageBox) return
      this.$refs.messageBox.scrollTop = this.$refs.messageBox?.scrollHeight
    },
  },
}
</script>

<style lang="scss">
div.LKRoadmapsFeedDialog {
  height: 80vh;
  max-height: 100vh;

  @media (max-width: 767px) {
    height: 100vh;
  }

  .chat {
    &__header {
      display: flex;
      justify-content: flex-start;
      padding: 15px;
    }

    &__header__icon-toggle {
      top: 8px;
      margin-right: 10px;
      opacity: 0.5;

      @media (max-width: 991px) {
        top: 0;
      }
    }

    &__header__close {
      position: absolute;
      top: 20px;
      right: 10px;
      display: block;
      color: $webflow-color-black;
      transition: transform 0.3s ease;

      @media (max-width: 991px) {
        top: 12px;
      }

      &:hover {
        transform: rotate(180deg);
      }
    }

    &__theme {
      flex-shrink: 0;
      max-height: 0;
      padding: 0;
      overflow: hidden;
      transition: max-height 0.5s;

      &-inner {
        padding: 10px;
      }

      &.is-active {
        max-height: 50%;
      }
    }

    &__messages {
      width: 100%;
      height: 100%;
      min-height: auto;
      max-height: none;
      padding: 15px;
      padding-bottom: 0;

      &__status {
        margin-bottom: 20px;
        color: $webflow-color-grey;
        text-align: center;
      }

      &__status-text {
        font-weight: bold;
      }
    }

    &__message {
      &_continuous {
        margin-top: -10px;
        border-radius: 20px;
      }

      &.is-new {
        animation: LKRoadmapsFeedDialog-glow linear 1s;
      }
    }
  }

  .filter {
    margin-right: 0;
    margin-bottom: 5px;
    cursor: default;

    &:last-child {
      margin-bottom: 0;
    }

    @media (max-width: 600px) {
      margin: 2px;
    }

    &__option-group {
      max-width: 100%;
      text-overflow: ellipsis;
    }
  }

  .textarea--chat {
    @media (max-width: 600px) {
      min-height: 60px;
      margin-bottom: 10px;
    }
  }

  .chat__send {
    padding: 20px 40px;

    @media (max-width: 600px) {
      padding: 10px;
    }
  }
}

@keyframes LKRoadmapsFeedDialog-glow {
  0% {
    box-shadow: 0 0 10px 2px rgba(0, 0, 255, 0.4);
  }

  100% {
    box-shadow: 0 0 10px 2px rgba(white, 0.4);
  }
}
</style>
